import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Form, Button, Col } from 'react-bootstrap';
import FormContainer from '../Components/FormContainer';
import CheckoutSteps from '../Components/CheckoutSteps';
import {
  saveShippingAddress,
  saveGuestInfo,
  setFulfillmentMethod,
} from '../slices/cartSlice';
import { Link } from 'react-router-dom';
import BackButton from "../Components/BackButton";
import { PICKUP_OPTIONS, CA_PROVINCES } from '../utils/shippingPolicy';
import { loadPrefs } from '../utils/orderPreferences';

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || '').trim());

export default function ShippingScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { cart } = useSelector((state) => ({
    cart: state.cart,
  }));
  const { userInfo } = useSelector((state) => state.auth);

  const {
    shippingAddress = {},
    guestMode,
    guestInfo = {},
    fulfillmentMethod: storedMethod = 'ship',
    pickupLocation: storedPickup,
  } = cart;

  const isGuest = new URLSearchParams(location.search).get('guest') === '1' || !!guestMode;
  const hasFreeDelivery = !isGuest && Boolean(userInfo?.freeDelivery);

  // Load per-user preferences (synchronous localStorage read, fast)
  const prefs = useMemo(
    () => (!isGuest && userInfo?._id ? loadPrefs(userInfo._id) : {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userInfo?._id, isGuest]
  );

  // Fulfillment — prefer explicit cart value; fall back to saved preference
  const [method, setMethod] = useState(() => {
    if (storedMethod === 'pickup') return 'pickup';
    // If cart has a stored ship address the user previously used ship mode
    if (shippingAddress?.address) return 'ship';
    return prefs.defaultFulfillmentMethod || 'ship';
  });

  const [pickupLocation, setPickupLocation] = useState(
    storedPickup || prefs.defaultPickupLocation || PICKUP_OPTIONS[0].value
  );

  // Contact — pre-fill from userInfo for authenticated users
  const [contactName, setContactName] = useState(
    guestMode ? (guestInfo.name || '') : (userInfo?.name || '')
  );
  const [contactEmail, setContactEmail] = useState(
    guestMode ? (guestInfo.email || '') : (userInfo?.email || '')
  );
  const [contactPhone, setContactPhone] = useState(
    guestMode ? (guestInfo.phone || '') : (prefs.defaultPhone || '')
  );

  // Address — cart value takes priority; then saved preference; then empty
  const defaultAddr = prefs.defaultShippingAddress || {};
  const [address, setAddress]       = useState(shippingAddress.address   || defaultAddr.address   || '');
  const [address2, setAddress2]     = useState(shippingAddress.address2  || defaultAddr.address2  || '');
  const [city, setCity]             = useState(shippingAddress.city      || defaultAddr.city      || '');
  const [province, setProvince]     = useState(
    shippingAddress.province || shippingAddress.state || defaultAddr.province || 'ON'
  );
  const [postalCode, setPostalCode] = useState(shippingAddress.postalCode || defaultAddr.postalCode || '');
  const [country, setCountry]       = useState(() => {
    const stored = shippingAddress.country;
    if (stored && stored.length <= 3) return stored;
    return defaultAddr.country || 'CA';
  });

  // Persist fulfillment choice in the cart as the user toggles
  useEffect(() => {
    dispatch(setFulfillmentMethod({ method, pickupLocation }));
  }, [method, pickupLocation, dispatch]);

  // If no longer in guest mode, clear any residual guest info
  useEffect(() => {
    if (!guestMode && (guestInfo.name || guestInfo.email || guestInfo.phone)) {
      dispatch(saveGuestInfo({ name: '', email: '', phone: '' }));
    }
  }, [guestMode, guestInfo?.name, guestInfo?.email, guestInfo?.phone, dispatch]);

  const submitHandler = (e) => {
    e.preventDefault();

    // Contact validation
    if (!contactName.trim() || !emailOk(contactEmail)) {
      window.alert('Please enter a valid full name and email.');
      return;
    }
    // Phone is required for BOTH ship and pickup (carriers need it; returns need it)
    if (!String(contactPhone || '').trim()) {
      window.alert('Please enter a phone number.');
      return;
    }

    // Save contact info (used by emails, invoice, admin views)
    dispatch(
      saveGuestInfo({
        name: contactName.trim(),
        email: contactEmail.trim(),
        phone: String(contactPhone || '').trim(),
      })
    );

    if (method === 'pickup') {
      // Minimal address for pickup so downstream never crashes
      dispatch(
        saveShippingAddress({
          fullName: contactName.trim(),
          phone: String(contactPhone || '').trim(),
          address: '',
          address2: '',
          city: '',
          province: '',
          postalCode: '',
          country: '',
        })
      );
      dispatch(setFulfillmentMethod({ method: 'pickup', pickupLocation }));
    } else {
      // Normalize country to ISO-2 if possible
      let c = String(country || '').trim().toUpperCase();
      if (c.length > 2) {
        if (c.startsWith('CAN')) c = 'CA';
        else if (c.startsWith('US')) c = 'US';
      }
      // Require province/state for CA/US
      const needsState = ['CA', 'US'].includes(c);
      if (!address.trim() || !city.trim() || !postalCode.trim() || !c) {
        window.alert('Please complete the shipping address.');
        return;
      }
      if (needsState && !String(province || '').trim()) {
        window.alert('Please select/enter a province/state.');
        return;
      }

      dispatch(
        saveShippingAddress({
          fullName: contactName.trim(),
          phone: String(contactPhone || '').trim(),
          address: address.trim(),
          address2: address2.trim(),
          city: city.trim(),
          province: String(province || '').trim().toUpperCase(), // save 2-letter where possible
          postalCode: postalCode.replace(/\s+/g, '').toUpperCase(), // strip spaces + uppercase
          country: c,                                            // 'CA' | 'US' | other
        })
      );
      dispatch(setFulfillmentMethod({ method: 'ship' }));
    }

    navigate(isGuest ? '/guest-payment' : '/payment');
  };

  return (
    <>
    <BackButton />
    <FormContainer>
      <CheckoutSteps step1 step2 />
      <h1>Shipping</h1>

      <Form onSubmit={submitHandler}>
        {/* Fulfillment method */}
        <Form.Group className="my-3">
          <Form.Label as="legend">How would you like to receive your items?</Form.Label>
          <Col>
            <Form.Check
              type="radio"
              id="method-ship"
              name="fulfillment"
              className="my-1"
              label="Ship to address"
              value="ship"
              checked={method === 'ship'}
              onChange={() => setMethod('ship')}
            />
            <Form.Check
              type="radio"
              id="method-pickup"
              name="fulfillment"
              className="my-1"
              label="In-Store Pickup"
              value="pickup"
              checked={method === 'pickup'}
              onChange={() => setMethod('pickup')}
            />
          </Col>
          {method === 'ship' && (
            <Form.Text className="text-muted">
              {hasFreeDelivery ? (
                <>Free delivery is enabled for your account.</>
              ) : (
                <>
                  Local delivery in the Greater Ottawa Area is free for orders over $60 before tax and
                  $9.99 for orders under $60. See our{' '}
                  <Link to="/shipping/policy">Shipping & Delivery Policy</Link>.
                </>
              )}
            </Form.Text>
          )}
        </Form.Group>

        {/* Pickup location */}
        {method === 'pickup' && (
          <Form.Group controlId="pickupLocation" className="my-2">
            <Form.Label>Pickup location</Form.Label>
            <Form.Select
              value={pickupLocation}
              onChange={(e) => setPickupLocation(e.target.value)}
            >
              {PICKUP_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              We'll email you when your order is ready for pickup. Bring ID and order number.
            </Form.Text>
          </Form.Group>
        )}

        <hr className="my-3" />

        {/* Contact */}
        <Form.Group controlId="contactName" className="my-2">
          <Form.Label>Full Name</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter your full name"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            required
          />
        </Form.Group>

        <Form.Group controlId="contactEmail" className="my-2">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            placeholder="name@example.com"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            required
            isInvalid={contactEmail && !emailOk(contactEmail)}
          />
          <Form.Control.Feedback type="invalid">
            Please enter a valid email address.
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group controlId="phone" className="my-3">
          <Form.Label>
            Phone <span className="text-danger">*</span> (required for delivery/returns)
          </Form.Label>
          <Form.Control
            type="tel"
            placeholder="e.g., 613-555-0123"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            required
          />
          <Form.Text className="text-muted">
            Carriers require a recipient phone for delivery and returns.
          </Form.Text>
        </Form.Group>

        {/* Ship-to address */}
        {method === 'ship' && (
          <>
            <hr className="my-3" />

            <Form.Group controlId="address" className="my-2">
              <Form.Label>Address</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group controlId="address2" className="my-2">
              <Form.Label>Unit / Apt / Suite (optional)</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. Apt 3B, Unit 12"
                value={address2}
                onChange={(e) => setAddress2(e.target.value)}
              />
            </Form.Group>

            <Form.Group controlId="city" className="my-2">
              <Form.Label>City</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="my-2" controlId="province">
              <Form.Label>Province / State</Form.Label>
              {String(country || '').toUpperCase() === 'CA' ? (
                <Form.Select
                  value={province}
                  onChange={(e) => setProvince(e.target.value)}
                  required
                >
                  <option value="">Select province</option>
                  {CA_PROVINCES.map(p => (
                    <option key={p.code} value={p.code}>{p.name}</option>
                  ))}
                </Form.Select>
              ) : (
                <Form.Control
                  type="text"
                  placeholder="e.g., NY"
                  value={province}
                  onChange={(e) => setProvince(e.target.value.toUpperCase())}
                  required={['CA', 'US'].includes(String(country || '').toUpperCase())}
                />
              )}
            </Form.Group>

            <Form.Group controlId="postalCode" className="my-2">
              <Form.Label>Postal Code</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter postal code"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group controlId="country" className="my-2">
              <Form.Label>Country</Form.Label>
              <Form.Control
                type="text"
                placeholder="CA"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                required
              />
              <Form.Text className="text-muted">Use country code like "CA" or "US".</Form.Text>
            </Form.Group>
          </>
        )}

        <Button type="submit" variant="primary" className="my-3">
          Continue
        </Button>
        <div className="mt-1">
          <small className="text-muted">
            See our <Link to="/shipping/policy">Shipping & Delivery Policy</Link> and{' '}
            <Link to="/returns/policy">Return & Refund Policy</Link>.
          </small>
        </div>
      </Form>
    </FormContainer>
    </>
  );
}
